import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core'
import { InvitationCollectionSendBulkData, InvitationCollectionSummary } from '@app/v2/event/types/invitation-tracker.type'
import { buildAddSkippedRows, getAddedEntriesCount } from '@app/v2/event/utils/invitation-collection-add.util'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ModalService } from '@app/v2/shared/services/modal.service'

const BULK_SEND_SUMMARY_SENT_HEADER_ICON = `<svg class="tw-w-3 tw-h-3 lg:tw-w-4 lg:tw-h-4 tw-shrink-0 tw-block" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g clip-path="url(#bulk_send_summary_check_clip)"><path d="M8 0C6.41775 0 4.87103 0.469192 3.55544 1.34824C2.23985 2.22729 1.21447 3.47672 0.608967 4.93853C0.00346627 6.40034 -0.15496 8.00887 0.153721 9.56072C0.462403 11.1126 1.22433 12.538 2.34315 13.6569C3.46197 14.7757 4.88743 15.5376 6.43928 15.8463C7.99113 16.155 9.59966 15.9965 11.0615 15.391C12.5233 14.7855 13.7727 13.7602 14.6518 12.4446C15.5308 11.129 16 9.58225 16 8C15.9977 5.87898 15.1541 3.8455 13.6543 2.34572C12.1545 0.845932 10.121 0.00232928 8 0ZM10.9656 6.9656L7.7656 10.1656C7.61558 10.3156 7.41213 10.3998 7.2 10.3998C6.98787 10.3998 6.78442 10.3156 6.6344 10.1656L5.0344 8.5656C4.88868 8.41472 4.80804 8.21263 4.80986 8.00288C4.81169 7.79312 4.89582 7.59247 5.04415 7.44414C5.19247 7.29582 5.39312 7.21168 5.60288 7.20986C5.81264 7.20804 6.01472 7.28867 6.1656 7.4344L7.2 8.4688L9.8344 5.8344C9.98528 5.68867 10.1874 5.60804 10.3971 5.60986C10.6069 5.61168 10.8075 5.69582 10.9559 5.84414C11.1042 5.99247 11.1883 6.19312 11.1901 6.40288C11.192 6.61263 11.1113 6.81472 10.9656 6.9656Z" fill="#111928"/></g><defs><clipPath id="bulk_send_summary_check_clip"><rect width="16" height="16" fill="white"/></clipPath></defs></svg>`

const BULK_SEND_SUMMARY_BLOCKED_HEADER_ICON = `<svg class="tw-w-3 tw-h-3 lg:tw-w-4 lg:tw-h-4 tw-shrink-0 tw-block" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g clip-path="url(#bulk_send_summary_info_clip)"><path d="M8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0ZM8.8 11.2C8.8 11.6418 8.44183 12 8 12C7.55817 12 7.2 11.6418 7.2 11.2V7.2C7.2 6.75817 7.55817 6.4 8 6.4C8.44183 6.4 8.8 6.75817 8.8 7.2V11.2ZM8 5.2C7.44772 5.2 7 4.75228 7 4.2C7 3.64772 7.44772 3.2 8 3.2C8.55228 3.2 9 3.64772 9 4.2C9 4.75228 8.55228 5.2 8 5.2Z" fill="#111928"/></g><defs><clipPath id="bulk_send_summary_info_clip"><rect width="16" height="16" fill="white"/></clipPath></defs></svg>`

type BulkSendSkipRow = {
  count: number
  label: string
}

type BulkSendSummaryView = 'sent' | 'before-send-blocked' | 'after-send-empty' | 'added' | 'add-empty'

@Component({
  selector: 'app-bulk-send-summary-modal',
  standalone: true,
  imports: [ModalComponent],
  templateUrl: './bulk-send-summary-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BulkSendSummaryModalComponent {
  public readonly id = input.required<string>()
  public readonly summary = input<InvitationCollectionSummary | null>(null)

  public readonly closed = output<void>()

  public readonly isAddMode = computed(() => this.summary()?.kind === 'add')

  public readonly summaryState = computed((): BulkSendSummaryView => {
    const summary = this.summary()
    if (summary?.kind === 'add') return this._affectedCount() > 0 ? 'added' : 'add-empty'
    if (this._affectedCount() > 0) return 'sent'
    return summary?.source === 'before-send' ? 'before-send-blocked' : 'after-send-empty'
  })

  public readonly header = computed(() => {
    switch (this.summaryState()) {
      case 'before-send-blocked':
      case 'after-send-empty':
        return "Invitations can't be sent"
      case 'added':
        return 'Entries are being added'
      case 'add-empty':
        return "Entries can't be added"
      default:
        return 'Invitations are being sent'
    }
  })

  public readonly headerIcon = computed(() => {
    const state = this.summaryState()
    return state === 'sent' || state === 'added' ? BULK_SEND_SUMMARY_SENT_HEADER_ICON : BULK_SEND_SUMMARY_BLOCKED_HEADER_ICON
  })

  public readonly countLabel = computed(() => this._affectedCount().toLocaleString())

  public readonly entriesWord = computed(() => (this._affectedCount() === 1 ? 'entry is' : 'entries are'))

  public readonly skipRows = computed((): BulkSendSkipRow[] => {
    const summary = this.summary()
    if (!summary) return []
    return summary.kind === 'add' ? buildAddSkippedRows(summary.result) : this._sendSkipRows(summary.data)
  })

  public readonly widthClass = computed(() => (this.isAddMode() ? 'tw-w-[95vw] sm:tw-w-[600px]' : 'tw-w-[343px] lg:tw-w-[600px]'))

  private readonly _affectedCount = computed(() => {
    const summary = this.summary()
    if (!summary) return 0
    return summary.kind === 'add' ? getAddedEntriesCount(summary.result) : summary.data.invited_count ?? 0
  })

  private readonly _modalService = inject(ModalService)

  /**
   * Closes the modal via Done and notifies the parent.
   */
  public onDone(): void {
    this._closeModal()
    this.closed.emit()
  }

  /**
   * Handles dismiss from header X or backdrop.
   */
  public onModalClose(): void {
    this.closed.emit()
  }

  private _closeModal(): void {
    if (this._modalService.isModalOpen(this.id())) {
      this._modalService.toggleModal(this.id())
    }
  }

  private _sendSkipRows(data: InvitationCollectionSendBulkData): BulkSendSkipRow[] {
    const rows: BulkSendSkipRow[] = [
      { count: data.no_contact_info ?? 0, label: 'No contact info (Skipped)' },
      { count: data.declined_count, label: 'Invitation declined (Skipped)' },
      { count: data.opt_out_count, label: 'Opted out (Skipped)' },
      { count: data.registered_count, label: 'Already registered for this persona (Skipped)' },
      { count: data.unreachable_count ?? 0, label: 'Unreachable (Skipped)' },
      { count: data.bounced_count ?? 0, label: 'Bounced (Skipped)' },
      { count: data.failed_count ?? 0, label: 'Failed to send (Skipped)' }
    ]
    return rows.filter((row) => row.count > 0)
  }
}
